import { useEffect, useState } from "react";
import axiosInstance from "config/axiosInstance";
import { Card } from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { IoTrendingUp, IoTrendingDown } from "react-icons/io5";
import colors from "assets/theme/base/colors";

function TrendsChart() {
  const [loading, setLoading] = useState(true);
  const [trendsData, setTrendsData] = useState([]);
  const [comparison, setComparison] = useState({ change: 0, direction: "up" });

  useEffect(() => {
    const fetchTrends = async () => {
      try {
        const res = await axiosInstance.get("/analytics/tendencias");

        if (res.data?.success) {
          const { mesActual = [], mesAnterior = [] } = res.data;

          if (mesActual.length === 0 && mesAnterior.length === 0) {
            setLoading(false);
            return;
          }
          
          // Process data for chart
          const currentTotal = mesActual.reduce((sum, d) => sum + parseFloat(d.total || 0), 0);
          const previousTotal = mesAnterior.reduce((sum, d) => sum + parseFloat(d.total || 0), 0);
          
          const change = previousTotal > 0 
            ? ((currentTotal - previousTotal) / previousTotal) * 100 
            : 0;
          
          setComparison({
            change: Math.abs(change).toFixed(1),
            direction: change >= 0 ? "up" : "down",
            currentTotal,
            previousTotal
          });

          // Combine data by day of month (extract day from date)
          const combinedData = [];
          
          // Create maps for quick lookup by day number (día ya viene como número del backend)
          const currentByDay = {};
          mesActual.forEach(item => {
            const day = parseInt(item.dia); // Ya es el número del día (1-31)
            currentByDay[day] = { total: parseFloat(item.total || 0), cantidad: parseInt(item.cantidad || 0) };
          });
          
          const previousByDay = {};
          mesAnterior.forEach(item => {
            const day = parseInt(item.dia);
            previousByDay[day] = { total: parseFloat(item.total || 0), cantidad: parseInt(item.cantidad || 0) };
          });
          
          // Determine number of days in current month
          // Always use 31 days to show the full month comparison
          const daysInMonth = 31;
          
          // Create combined data with ALL days of the month (1-31)
          for (let day = 1; day <= daysInMonth; day++) {
            const current = currentByDay[day] || { total: 0, cantidad: 0 };
            const previous = previousByDay[day] || { total: 0, cantidad: 0 };
            
            combinedData.push({
              dia: day,
              mesActual: current.total,
              mesAnterior: previous.total,
              cantidadActual: current.cantidad,
              cantidadAnterior: previous.cantidad
            });
          }
          
          setTrendsData(combinedData);
        }
        
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    fetchTrends();
  }, []);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <VuiBox
          sx={{
            background: "linear-gradient(127.09deg, rgba(6, 11, 40, 0.94) 19.41%, rgba(10, 14, 35, 0.94) 76.65%)",
            backdropFilter: "blur(42px)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "8px",
            p: 2
          }}
        >
          <VuiTypography variant="caption" color="white" fontWeight="bold">
            Día {label}
          </VuiTypography>
          {payload.map((entry, index) => (
            <VuiTypography key={index} variant="caption" color="text" display="block">
              <span style={{ color: entry.color }}>●</span> {entry.name}: S/ {entry.value.toFixed(2)}
            </VuiTypography>
          ))}
        </VuiBox>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Card sx={{ height: "100%", p: 3 }}>
        <VuiTypography variant="lg" color="white" fontWeight="bold">
          Cargando tendencias...
        </VuiTypography>
      </Card>
    );
  }

  if (!trendsData || trendsData.length === 0) {
    return (
      <Card sx={{ height: "100%", p: 3 }}>
        <VuiTypography variant="lg" color="white" fontWeight="bold" mb={2}>
          Comparación de Tendencias
        </VuiTypography>
        <VuiTypography variant="button" color="text">
          No hay suficientes datos para mostrar tendencias. Necesitas depósitos en el mes actual y anterior.
        </VuiTypography>
      </Card>
    );
  }

  return (
    <Card sx={{ height: "100%", p: 3 }}>
      <VuiBox mb={3}>
        <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <VuiTypography variant="lg" color="white" fontWeight="bold">
            Comparación de Tendencias
          </VuiTypography>
          <VuiBox
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              background: comparison.direction === "up" 
                ? "linear-gradient(127.09deg, rgba(13, 218, 136, 0.2) 0%, rgba(13, 218, 136, 0.05) 100%)"
                : "linear-gradient(127.09deg, rgba(255, 56, 56, 0.2) 0%, rgba(255, 56, 56, 0.05) 100%)",
              borderRadius: "12px",
              px: 2,
              py: 1
            }}
          >
            {comparison.direction === "up" ? (
              <IoTrendingUp size={20} color="#0DDA88" />
            ) : (
              <IoTrendingDown size={20} color="#FF3838" />
            )}
            <VuiTypography
              variant="button"
              color={comparison.direction === "up" ? "success" : "error"}
              fontWeight="bold"
            >
              {comparison.direction === "up" ? "+" : "-"}{comparison.change}%
            </VuiTypography>
          </VuiBox>
        </VuiBox>
        <VuiTypography variant="button" color="text">
          Mes actual vs mes anterior
        </VuiTypography>
      </VuiBox>

      <VuiBox sx={{ width: "100%", height: 300 }}>
        <ResponsiveContainer>
          <LineChart
            data={trendsData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
            <XAxis
              dataKey="dia"
              stroke={colors.white.main}
              style={{ fontSize: "12px" }}
              label={{ value: "Día del mes", position: "insideBottom", offset: -5, fill: colors.white.main }}
            />
            <YAxis
              stroke={colors.white.main}
              style={{ fontSize: "12px" }}
              label={{ value: "Monto (S/)", angle: -90, position: "insideLeft", fill: colors.white.main }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ color: colors.white.main }}
              iconType="circle"
            />
            <Line
              type="monotone"
              dataKey="mesActual"
              name="Mes Actual"
              stroke="#0075FF"
              strokeWidth={3}
              dot={{ fill: "#0075FF", r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line
              type="monotone"
              dataKey="mesAnterior"
              name="Mes Anterior"
              stroke="#0DDA88"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: "#0DDA88", r: 3 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </VuiBox>

      <VuiBox mt={3} display="flex" justifyContent="space-around">
        <VuiBox textAlign="center">
          <VuiTypography variant="caption" color="text">
            Total Mes Actual
          </VuiTypography>
          <VuiTypography variant="h4" color="white" fontWeight="bold">
            S/ {comparison.currentTotal?.toFixed(2) || "0.00"}
          </VuiTypography>
        </VuiBox>
        <VuiBox textAlign="center">
          <VuiTypography variant="caption" color="text">
            Total Mes Anterior
          </VuiTypography>
          <VuiTypography variant="h4" color="white" fontWeight="bold">
            S/ {comparison.previousTotal?.toFixed(2) || "0.00"}
          </VuiTypography>
        </VuiBox>
      </VuiBox>
    </Card>
  );
}

export default TrendsChart;
